import { useState, useEffect, useRef } from 'react';
import { Form } from 'antd';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMessage } from '@context/MessageProvider';
import { otpSchema } from '@validators/outside.schema';
import { useAuthStore } from '@stores/auth.store';
import { getCurrentUserDashboardRoute } from '@utils/routing';
import {
    useVerifyOtpMutation,
    useVerifyEmailMutation,
    useResendVerificationMutation,
    useRequestPasswordResetMutation,
} from './useAuthMutations';

const RESEND_COOLDOWN_EMAIL_VERIFY = 60;
const RESEND_COOLDOWN_OTP = 120;

export const useOtp = () => {
    const [form] = Form.useForm();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { showMessage } = useMessage();
    const email = searchParams.get('email');
    const isEmailVerification = searchParams.get('verify') === 'email';
    const [countdown, setCountdown] = useState(
        isEmailVerification ? RESEND_COOLDOWN_EMAIL_VERIFY : RESEND_COOLDOWN_OTP,
    );
    const verifyOtpMutation = useVerifyOtpMutation();
    const requestResetOtpMutation = useRequestPasswordResetMutation();
    const verifyEmailMutation = useVerifyEmailMutation();
    const resendVerificationMutation = useResendVerificationMutation();
    const hasAutoSentResend = useRef(false);

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const handleResendOtp = async () => {
        if (!email) {
            showMessage('error', 'Email is required');
            return;
        }

        try {
            if (isEmailVerification) {
                await resendVerificationMutation.mutateAsync({ email });
                setCountdown(RESEND_COOLDOWN_EMAIL_VERIFY);
            } else {
                await requestResetOtpMutation.mutateAsync(email);
                setCountdown(RESEND_COOLDOWN_OTP);
            }
        } catch (error) {
            // Error handling is done in the mutation
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const onFinish = async (values) => {
        if (loading) return;
        if (!email) {
            showMessage('error', 'Email is required');
            return;
        }

        try {
            const parsedValues = otpSchema.parse(values);
            if (isEmailVerification) {
                const result = await verifyEmailMutation.mutateAsync({ email, otp: parsedValues.otp });
                useAuthStore.getState().setSessionFromVerifyEmail(result);
                const isOnboarded = result?.data?.user?.isOnboarded === true;
                if (isOnboarded) {
                    const dashboardRoute = getCurrentUserDashboardRoute();
                    navigate(dashboardRoute || '/dashboard', { replace: true });
                } else {
                    navigate('/onboarding', { replace: true });
                }
            } else {
                await verifyOtpMutation.mutateAsync({ email, otp: parsedValues.otp });
                navigate(`/reset-password?email=${encodeURIComponent(email)}&otp=${encodeURIComponent(parsedValues.otp)}`, {
                    replace: true,
                });
            }
        } catch (error) {
            if (error instanceof z.ZodError) {
                form.setFields([{ name: 'otp', errors: [error.errors[0].message] }]);
            } else if (error?.message === 'OTP incorrect') {
                form.setFields([{ name: 'otp', errors: ['OTP incorrect'] }]);
            }
        }
    };

    const loading = isEmailVerification ? verifyEmailMutation.isPending : verifyOtpMutation.isPending;
    const resendLoading = isEmailVerification ? resendVerificationMutation.isPending : requestResetOtpMutation.isPending;

    return {
        form,
        loading,
        countdown,
        resendLoading,
        email,
        onFinish,
        handleResendOtp,
        formatTime,
    };
};
